'use client';
import { type FC, useEffect } from 'react';

import { localeAtom } from '@core/Atoms/Locale/Locale.atom';
import { tenantAtom } from '@core/Atoms/Tenant/Tenant.atom';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import { clientLog } from '@core/Utils/Log/ClientLog';
import '@core/Utils/Log/Log';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { usePathname } from 'next/navigation';

import { experimentsAtom } from '@growthBookExperimentation/Atoms/Experiments.atom';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import type { AllExperiments } from '@growthBookExperimentation/Entities/Experiments.entities';
import type { AllFlags } from '@growthBookExperimentation/Entities/FlagKeys.entities';
import { valuationAtom } from '@valuation/Atoms/Valuation.atom';

import { hasMountedOnPdpAtom } from 'Atoms/App/Pdp/hasMountedOnPdp.atom';
import { hasMountedAtom } from 'Atoms/Mounted/hasMounted.atom';

type Props = {
  flags: AllFlags;
  locale: Locale;
  tenant?: Tenant | undefined;
  experiments?: AllExperiments;
};

export const AppLogic: FC<Props> = ({ locale, tenant, flags, experiments = [] }) => {
  const setLocale = useSetAtom(localeAtom);
  const setTenant = useSetAtom(tenantAtom);
  const pathname = usePathname();
  const [hasMountedOnPdp, sethasMountedOnPdp] = useAtom(hasMountedOnPdpAtom);

  useHydrateAtoms(
    [
      [flagsAtom, flags],
      [experimentsAtom, experiments],
    ],
    { dangerouslyForceHydrate: true },
  );

  setLocale(locale);
  setTenant(tenant);

  const [valuation, setValuation] = useAtom(valuationAtom);
  const hasMounted = useAtomValue(hasMountedAtom);

  useEffect(() => {
    if (!hasMounted) return;
    // this is to identify if the user has visited the PDP page for the first time
    if (pathname.includes('/auto/') && !hasMountedOnPdp) {
      sethasMountedOnPdp(true);
    }

    if (valuation === undefined) setValuation(null);
  }, [hasMounted]);

  useEffect(() => {
    clientLog({ label: 'flags', data: flags, color: '#fef8eb' });
    clientLog({ label: 'experiments', data: experiments, color: '#fef8eb' });
  }, [flags, experiments]);

  return null;
};

AppLogic.displayName = 'AppLogic';
